/**
 * The profile (docs/plans/people.md P1–P5): which tabs a viewer gets, what each status / pay type reads as, days on
 * site, the bill rate a role carries, and the lines the Pay / Employment tabs show. Pure — no I/O. The books hold the
 * person; the screens call `getPerson` and pass its answer here.
 *
 * Privacy (Lj, Oct 11): contact + emergency — the person and admins only; pay — your own, or an admin. The server
 * already refuses the rest (books getPerson, ops sync §3.9); these only decide what to draw.
 */

export const PROFILE_TABS = [
  { key: 'overview', label: 'Overview' },
  { key: 'contact', label: 'Contact' },
  { key: 'pay', label: 'Pay' },
  { key: 'access', label: 'Access' },
  { key: 'employment', label: 'Employment' },
  { key: 'activity', label: 'Activity' },
];

/** The tabs this viewer may open on this person: everyone gets Overview, Employment (status only) and Activity. */
export function profileTabs({ viewerUid, uid, isAdmin = false, assistant = false } = {}) {
  const own = !!viewerUid && viewerUid === uid;
  const priv = own || !!isAdmin;
  return PROFILE_TABS.filter((t) => {
    if (t.key === 'contact' || t.key === 'pay' || t.key === 'access') return priv && !(assistant && t.key === 'pay');
    return true;
  });
}

export const EMPLOYMENT_LABELS = { active: 'Active', leave: 'On leave', laid_off: 'Laid off', terminated: 'Let go', past: 'Past' };
export const ENDED = new Set(['laid_off', 'terminated', 'past']);
export const PERSON_PAY_LABELS = { hourly: 'Hourly', salary: 'Salary', sub: 'Contractor', share: 'Task share', piece: 'Piece', unpaid: 'Unpaid (owner)' };
export const PERSON_PAY_TYPES = ['salary', 'hourly', 'sub', 'share', 'piece', 'unpaid'];   // Lj's three first, then "more"
/** Employees on payroll: hourly / salary, and piece / task share (paid on an hourly floor, topped up to minimum wage). */
export const PAYROLL_PAY_TYPES = new Set(['hourly', 'salary', 'piece', 'share']);
export const CONTRACTOR_PAY_LABELS = { quote: 'By quote (fixed)', hourly: 'Hourly' };
export const END_REASONS = [
  { key: 'laid_off', label: 'Laid off — may come back' },
  { key: 'past', label: 'Quit / contract ended' },
  { key: 'terminated', label: 'Let go' },
];

/** A published team member (opsMembers) whose employment ended — kept for history, off every list of who can work. */
export const isPastMember = (m) => !!m && (m.active === false || ENDED.has(m.status));
/** The team that can work: no past people. */
export const currentMembers = (members = []) => members.filter((m) => !isPastMember(m));
/** The past people, most recently ended first. */
export const pastMembers = (members = []) => members.filter(isPastMember).sort((a, b) => (b.endedAt || 0) - (a.endedAt || 0));

/** The status chip: label + tone ('ok' | 'warn' | 'off'). */
export function statusChip(status) {
  const s = EMPLOYMENT_LABELS[status] ? status : 'active';
  return { status: s, label: EMPLOYMENT_LABELS[s], tone: s === 'active' ? 'ok' : s === 'leave' ? 'warn' : 'off' };
}

const localYmd = (ms) => { const d = new Date(ms); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };

/**
 * Days on site (Lj: track a contractor's DAYS ON SITE): the distinct local days with a clocked work session on a job —
 * a project that isn't internal / personal. Drives and overlays don't count. → { days, dates: [YYYY-MM-DD], byProject }
 */
export function daysOnSite(sessions = [], uid, { projects = [], from = 0, to = Infinity, isJob = null } = {}) {
  const job = isJob || ((p) => !!p && !p.internal && p.jobType !== 'internal' && p.jobType !== 'personal' && !p.personal);
  const byId = new Map(projects.map((p) => [p.id, p]));
  const dates = new Set();
  const byProject = new Map();
  for (const s of sessions) {
    if (!s || s.ownerUid !== uid || s.type === 'travel' || s.overlay || !s.projectId) continue;
    const at = Number(s.startedAt);
    if (!Number.isFinite(at) || at < from || at >= to) continue;
    if (!job(byId.get(s.projectId))) continue;
    const d = s.date || localYmd(at);
    dates.add(d);
    if (!byProject.has(s.projectId)) byProject.set(s.projectId, new Set());
    byProject.get(s.projectId).add(d);
  }
  return {
    days: dates.size, dates: [...dates].sort(),
    byProject: [...byProject].map(([projectId, set]) => ({ projectId, days: set.size })).sort((a, b) => b.days - a.days),
  };
}

/**
 * What an hour of this person bills at (Lj: "billable rates by role/title — an apprentice isn't charged at the journeyman
 * rate"): this project's rate for their role (Project settings → How this job pays, `billRates`), else the project's
 * one bill rate (`hourlyRate`; legacy `billRate`), else their role's rate from the books' table (opsRates/billRoles,
 * admins only). Never their pay rate. → { rate, from: 'project-role' | 'project' | 'role' | null, role }
 */
export function billRateFor(person, roles = [], project = null) {
  const role = person?.tradeRole ? (roles || []).find((r) => r.id === person.tradeRole) || null : null;
  const perRole = person?.tradeRole ? Number(project?.billRates?.[person.tradeRole]) : 0;
  if (perRole > 0) return { rate: perRole, from: 'project-role', role };
  const own = Number(project?.hourlyRate) > 0 ? Number(project.hourlyRate) : Number(project?.billRate);
  if (own > 0) return { rate: own, from: 'project', role };
  if (role && Number(role.billRate) > 0) return { rate: Number(role.billRate), from: 'role', role };
  return { rate: null, from: null, role };
}

/** The share weight for splitting a task (P7): the person's own %, else their role's, else 100. */
export function shareWeight(person, roles = []) {
  if (Number.isFinite(person?.sharePct) && person.sharePct >= 0) return person.sharePct;
  const role = person?.tradeRole ? (roles || []).find((r) => r.id === person.tradeRole) : null;
  return Number.isFinite(role?.sharePct) && role.sharePct >= 0 ? role.sharePct : 100;
}

const money = (n) => (n == null || !Number.isFinite(Number(n)) ? null : `$${Number(n).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

/**
 * The Pay tab's lines from getPerson (+ opsPay for the loaded cost): [{ label, value, sub? }]. Empty when the viewer
 * can't see pay (the server sent none).
 */
export function payLines(person, pay = null) {
  if (!person?.canViewPrivate) return [];
  const t = person.payType || pay?.payType || null;
  const p = person.payroll || null;
  const out = [{ label: 'Pay type', value: t ? PERSON_PAY_LABELS[t] || t : 'Not set' }];
  if (t === 'hourly' || t === 'piece' || t === 'share') out.push({ label: t === 'hourly' ? 'Wage' : 'Hourly floor', value: p?.hourlyRate != null ? `${money(p.hourlyRate)} /h` : 'Not set', sub: p ? 'payroll' : 'no payroll profile yet' });
  if (t === 'salary') out.push({ label: 'Salary', value: p?.salariedGross != null ? `${money(p.salariedGross)} / pay period` : 'Not set', sub: p?.standardHoursPerPeriod ? `${p.standardHoursPerPeriod} h per period` : null });
  if (t === 'sub') {
    out.push({ label: 'Paid', value: CONTRACTOR_PAY_LABELS[person.contractorPay] || 'Hourly' });
    if (person.costRatePerHour != null) out.push({ label: 'Rate', value: `${money(person.costRatePerHour)} /h` });
  }
  if (person.shadowRatePerHour != null) out.push({ label: t === 'unpaid' ? 'Shadow rate' : 'If paid out', value: `${money(person.shadowRatePerHour)} /h`, sub: 'never posted' });
  if (pay?.loadedCostPerHour != null && t !== 'unpaid') out.push({ label: 'Loaded cost', value: `${money(pay.loadedCostPerHour)} /h`, sub: pay.burden?.pct != null ? `+${Math.round(pay.burden.pct)}% employer costs` : null });
  if (t === 'share') out.push({ label: 'Paid by', value: 'Their share of each task’s labour', sub: 'fixed jobs · through payroll when BattleBooks says so' });
  if (p && PAYROLL_PAY_TYPES.has(t)) {
    out.push({ label: 'Payroll', value: [p.provinceOfEmployment, p.td1Set ? 'TD1 ✓' : 'TD1 basic', p.vacationRatePct != null ? `vac ${p.vacationRatePct}%` : null].filter(Boolean).join(' · ') });
  }
  if (person.billRole) {
    out.push({ label: 'Role', value: person.billRole.name, sub: [person.billRole.billRate != null ? `bills ${money(person.billRole.billRate)} /h` : null, `share ${Number.isFinite(person.sharePct) ? person.sharePct : person.billRole.sharePct ?? 100}%`].filter(Boolean).join(' · ') });
  }
  return out;
}

// ---- the pay and access editors' forms (the profile's Pay / Access tabs and the hire wizard share them) ------------

/** The Pay editor's form, from a books person (or blank for a hire). */
export function payFormOf(person = null) {
  const p = person?.payroll || {};
  const t = person?.payType || 'hourly';
  const str = (v) => (v == null ? '' : String(v));
  return {
    payType: t,
    rate: str(t === 'salary' ? p.salariedGross : t === 'sub' ? person?.costRatePerHour : p.hourlyRate),
    contractorPay: person?.contractorPay || 'hourly', std: str(p.standardHoursPerPeriod), vac: str(p.vacationRatePct),
    shadow: str(person?.shadowRatePerHour), tradeRole: person?.tradeRole || null, share: str(person?.sharePct),
  };
}

const numOrNull = (v) => (v === '' || v == null || !Number.isFinite(Number(v)) ? null : Number(v));

/** The form → books savePersonPay input (the one pay save). */
export function payInputOf(f) {
  return {
    payType: f.payType, rate: f.payType === 'unpaid' ? undefined : numOrNull(f.rate),
    ...(f.payType === 'sub' ? { contractorPay: f.contractorPay } : {}),
    ...(f.payType === 'salary' ? { standardHoursPerPeriod: numOrNull(f.std) } : {}),
    ...(PAYROLL_PAY_TYPES.has(f.payType) && f.vac !== '' && f.vac != null ? { vacationRatePct: numOrNull(f.vac) } : {}),
    shadowRatePerHour: numOrNull(f.shadow), tradeRole: f.tradeRole || null, sharePct: numOrNull(f.share),
  };
}

/** The rate field's label for a pay type. */
export function payRateLabel(f) {
  return {
    hourly: 'Wage $/h', salary: 'Salary $ per pay period', piece: 'Hourly floor $/h (≥ minimum wage)',
    share: 'Hourly floor $/h (≥ minimum wage)', sub: f?.contractorPay === 'quote' ? 'Rate $/h (optional — for costing)' : 'Their rate $/h',
  }[f?.payType] || 'Rate';
}

/** What the books would refuse, said before the save (the server checks again). null = fine. */
export function payFormProblem(f) {
  const rate = numOrNull(f?.rate);
  if (!f?.payType) return 'Pick how they are paid.';
  if (f.rate !== '' && f.rate != null && rate == null) return 'The rate must be a number.';
  if (rate != null && rate < 0) return 'The rate can’t be negative.';
  if (PAYROLL_PAY_TYPES.has(f.payType) && !(rate > 0)) return f.payType === 'salary' ? 'Salary per pay period is required.' : f.payType === 'hourly' ? 'The hourly wage is required.' : 'The hourly floor (at least minimum wage) is required.';
  if (f.payType === 'salary' && !(numOrNull(f.std) > 0)) return 'Standard hours per pay period are required for a salary.';
  if (f.payType === 'sub' && f.contractorPay !== 'quote' && !(rate > 0)) return 'An hourly contractor needs their rate.';
  const share = numOrNull(f.share);
  if (f.share !== '' && f.share != null && (share == null || share < 0 || share > 300)) return 'Share % must be between 0 and 300.';
  return null;
}

/** The Access editor's form, from a books person (or a blank hire: crew, BattleOps only, nothing granted yet). */
export function accessFormOf(person = null) {
  if (!person) return { role: 'painter', apps: ['ops'], everything: false, grants: {}, depts: [] };
  return { role: person.role, apps: person.apps, everything: !person.access, grants: { ...(person.access?.departments || {}) }, depts: [...(person.departmentIds || [])] };
}

/** The form → books updateTeamMember / invite fields. An admin gets every app and everything. */
export function accessInputOf(f, { self = false, projects = {} } = {}) {
  const admin = f.role === 'admin';
  return {
    ...(self ? {} : { role: f.role, apps: admin ? null : f.apps, access: admin || f.everything ? null : { departments: { ...f.grants }, projects: { ...projects } } }),
    departmentIds: [...f.depts],
  };
}

const ACCESS_LEVELS = [null, 'view', 'work', 'manage'];
/** Tap a department: none → View → Work → Manage → none. */
export function cycleGrant(f, id) {
  const cur = f.everything ? null : f.grants[id] || null;
  const next = ACCESS_LEVELS[(ACCESS_LEVELS.indexOf(cur) + 1) % ACCESS_LEVELS.length];
  const grants = { ...(f.everything ? {} : f.grants) };
  if (next) grants[id] = next; else delete grants[id];
  return { ...f, everything: false, grants };
}

/** Toggle an app; all four on = null (every app). */
export function toggleAppIn(f, app, all = ['ops', 'books', 'bids', 'bmail']) {
  const has = (k) => f.apps === null || (f.apps || []).includes(k);
  const on = all.filter((k) => (k === app ? !has(k) : has(k)));
  return { ...f, apps: on.length === all.length ? null : on };
}

/** The Employment tab's lines. Everyone sees the status; the dates and reason only reach the person and admins. */
export function employmentLines(person, { daysOnSite: onSite = null } = {}) {
  const e = person?.employment || { status: 'active' };
  const out = [{ label: 'Status', value: EMPLOYMENT_LABELS[e.status] || 'Active' }];
  if (!person?.canViewPrivate) return out;
  if (e.startDate) out.push({ label: 'Started', value: e.startDate });
  if (e.status === 'leave' && e.recallDate) out.push({ label: 'Back', value: e.recallDate });
  if (ENDED.has(e.status)) {
    if (e.lastDayWorked) out.push({ label: 'Last day worked', value: e.lastDayWorked });
    if (e.endDate && e.endDate !== e.lastDayWorked) out.push({ label: 'Ended', value: e.endDate });
    if (e.recallDate) out.push({ label: 'Recall', value: e.recallDate });
    if (e.reason) out.push({ label: 'Reason', value: e.reason });
    out.push({ label: 'Their login', value: e.pastAccess === 'payslips' ? 'Payslips and T4s only' : 'None' });
  }
  if (person.title) out.push({ label: 'Title', value: person.title });
  if (person.payType) out.push({ label: 'Worker', value: person.payType === 'sub' ? 'Contractor' : person.payType === 'unpaid' ? 'Owner' : 'Employee' });
  if (onSite && (person.payType === 'sub' || onSite.days)) out.push({ label: 'Days on site', value: String(onSite.days), sub: 'days clocked on a job' });
  return out;
}

/** Unassigned when someone left (endEmployment): open tasks with `unassignedFrom`, for Team → Needs you. */
export function leftoverTasks(tasks = [], members = []) {
  const name = (uid) => members.find((m) => (m.uid || m.id) === uid)?.name || 'someone who left';
  const by = new Map();
  for (const t of tasks) {
    if (!t || t.assignee || t.status === 'done' || !t.unassignedFrom) continue;
    if (!by.has(t.unassignedFrom)) by.set(t.unassignedFrom, []);
    by.get(t.unassignedFrom).push(t);
  }
  return [...by].map(([uid, list]) => ({ uid, name: name(uid), tasks: list }));
}
